import { type Nullable } from '../../types/common'
import { NotificationComponent } from '../notification'
import { ProductBreadcrumbs } from '../../sections/product/product-breadcrumbs'
import {
  isExternalLink,
  isCurrentPageLink,
  createPrefetchLink,
  viewportObserver,
  $list,
  $el,
  $elParent,
  whenDefined,
  transitionToPromise,
  getTargets,
} from '../../utils/utils'
import { BaseComponent } from '../base-component'
import { removeStyleVariable, setStyleVariable } from 'src/scripts/utils/dom'
import { gradientOpacity } from 'src/scripts/sections/popups/utils'

const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')

export class BodyElement extends BaseComponent {
  notification: Nullable<NotificationComponent>
  overlayCalls: any[] = []
  isScrolling: boolean = false
  timeoutScrollId: ReturnType<typeof setTimeout>
  removeOverlayTimeoutId: ReturnType<typeof setTimeout>
  prefetchedLinks: string[] = []
  isVisible: boolean
  isFixed: boolean
  fixedTopPosition: number
  openedDialogWindows: string[] = []
  lastFocusedTarget: HTMLElement
  currentFocusEl: HTMLElement
  isUsingKeyboard: boolean = false
  zIndex: string
  scrollbarWidth: number
  isOpenedOnFirefox: boolean
  withGradient: boolean

  mountComponent(): void {
    this.setBreadcrumbs()
    this.setViewportObserver()
    this.setRegistrationMessage()
    this.setNotification()
    this.setThemeInfo()
    this.isFixed = false

    this.addListener(window, 'wheel', this.handleWindowWheel)
    this.addListener(window, 'beforeunload', this.handlePageChange)
    this.addListener(window, 'pagehide', this.handlePageHide)
    this.addListener(window, 'pageshow', this.handlePageShow)
    this.addListener(document, 'mouseover', this.handleLinkOver)
    this.addListener(document, 'mousedown', this.handleMousedown)
    this.addListener(document, 'keydown', this.handleKeydown)
    this.addListener(document, 'focusout', this.handleFocusout)

    this.removeLoadingOverlay('#GlobalLoadingOverlayDots')
    this.removeOverlayTimeoutId = setTimeout(() => {
      this.removeLoadingOverlay('#GlobalLoadingOverlayDots')
    }, 5000)

    this.setGradientOverlay()
  }

  unmountComponent(): void {
    clearTimeout(this.timeoutScrollId)
    clearTimeout(this.removeOverlayTimeoutId)
  }

  handleMousedown = (): void => {
    this.setUsingKeyboard(false)
  }

  handleKeydown = (): void => {
    this.setUsingKeyboard(true)
  }

  addDialogWindow(newWindowId: string): void {
    if (this.openedDialogWindows.length === 0) {
      const isNotWindowChild = this.openedDialogWindows.every(windowId => {
        return !this.currentFocusEl.closest(`[id="${windowId}"]`)
      })

      if (isNotWindowChild) {
        this.lastFocusedTarget = this.currentFocusEl || $el('#MainContent')
      }
    }

    if (!this.openedDialogWindows.includes(newWindowId)) {
      this.openedDialogWindows = [...this.openedDialogWindows, newWindowId]
    }
  }

  removeDialogWindow(removedWindowId: string) {
    this.openedDialogWindows = this.openedDialogWindows.filter(
      windowId => windowId !== removedWindowId,
    )
    const lastOpenedWindowId =
      this.openedDialogWindows[this.openedDialogWindows.length - 1]

    if (lastOpenedWindowId) {
      const lastOpenedWindow = $el(`#${lastOpenedWindowId}`)
      const { firstTarget } = getTargets(lastOpenedWindow)
      firstTarget?.focus()
    } else if (this.lastFocusedTarget) {
      this.lastFocusedTarget.focus()
      this.lastFocusedTarget = null
    }
  }

  isLastOpenedWindow(windowId: string): boolean {
    return (
      this.openedDialogWindows[this.openedDialogWindows.length - 1] === windowId
    )
  }

  setUsingKeyboard(usingKeyboard: boolean): void {
    document.body.classList.toggle('using-keyboard', usingKeyboard)
    this.isUsingKeyboard = usingKeyboard
  }

  handleFocusout = event => {
    const focusTarget = event.relatedTarget as HTMLElement

    if (focusTarget) {
      this.currentFocusEl = focusTarget
    }
  }

  setBreadcrumbs() {
    if (window.location.pathname.includes('products')) {
      customElements.define('product-breadcrumbs', ProductBreadcrumbs)

      const breadcrumbs: ProductBreadcrumbs[] = $list('product-breadcrumbs')

      if (breadcrumbs.length) {
        const page = localStorage.getItem('prevCollectionPage')
        breadcrumbs.forEach(el => el.setCollectionPage(page))
        localStorage.removeItem('prevCollectionPage')
      }
    } else if (!window.location.pathname.includes('collections')) {
      localStorage.removeItem('prevCollectionPage')
    }
  }

  setViewportObserver() {
    const sections = $list('.shopify-section')
    sections.forEach(section => {
      viewportObserver.observe(section)
    })
  }

  setThemeInfo() {
    window.auroraTheme = {
      ...window.auroraTheme,
      theme_version: '3.5.0',
      id: window.Shopify.theme.id,
      storeId: window.Shopify.theme.theme_store_id,
      isLicenseTheme: !!window.Shopify.theme.theme_store_id,
    }
  }

  handleWindowWheel = (): void => {
    if (this.timeoutScrollId) {
      clearTimeout(this.timeoutScrollId)
      this.isScrolling = true
    }

    this.timeoutScrollId = setTimeout(() => {
      this.isScrolling = false
    }, 200)
  }

  handleLinkOver = (event: MouseEvent): void => {
    const link: HTMLLinkElement = $elParent('a[href]', event.target)

    const href = link?.href

    if (!href) {
      return
    }

    if (link?.hasAttribute('without-prefetch-on-hover')) {
      return
    }

    const wasPrefetched = this.prefetchedLinks.includes(href)
    const isNotPrefetchetable = isExternalLink(href) || isCurrentPageLink(href)

    if (wasPrefetched || isNotPrefetchetable) {
      return
    }

    createPrefetchLink(href)
    this.prefetchedLinks.push(href)
  }

  handlePageChange = () => {
    this.showLoadingOverlay('#GlobalLoadingOverlayDots')
  }

  handlePageHide = () => {
    this.removeLoadingOverlay('#GlobalLoadingOverlayDots')
  }

  handlePageShow = () => {
    this.removeLoadingOverlay('#GlobalLoadingOverlayDots')
  }

  showLoadingOverlay = (selector: string) => {
    this.setScrollLock()

    const loadingOverlay = $el(selector)
    loadingOverlay.classList.remove('hidden')

    requestAnimationFrame(() => {
      loadingOverlay.style.opacity = '1'
    })
  }

  removeLoadingOverlay = async (selector: string) => {
    const loadingOverlay = $el(selector)

    if (loadingOverlay.classList.contains('hidden')) {
      return
    }

    loadingOverlay.style.opacity = '0'
    await transitionToPromise(loadingOverlay)
    loadingOverlay.classList.add('hidden')
    this.unsetScrollLock()
  }

  setNotification() {
    const notification: NotificationComponent = $el('#GlobalNotification')

    if (!notification || !window.notification) {
      return
    }

    const { text, variant, duration } = window.notification

    notification.show(text, variant, duration)
  }

  showOverlay(key: string, styles): void {
    this.zIndex = styles.zIndex ?? 3

    this.setScrollLock()

    this.setZindexVariable()
    this.setOverlayStyles(styles)

    if (styles.backdropFilter) {
      this.classList.add('with-custom-background')
    }

    this.classList.add('visible')
    this.isVisible = true

    this.overlayCalls = [...this.overlayCalls, { key, styles }]
  }

  async hideOverlay(key: string): Promise<void> {
    if (!this.isVisible) {
      return
    }

    this.overlayCalls = this.overlayCalls.filter(
      overlayCall => overlayCall.key !== key,
    )

    const lastCall = this.overlayCalls[this.overlayCalls.length - 1]

    if (lastCall) {
      this.setOverlayStyles(lastCall.styles)
    } else {
      this.isVisible = false
      this.setOverlayZindex()
      await transitionToPromise(this)

      if (this.isVisible === false) {
        this.unsetScrollLock()
        this.classList.remove('visible')
        this.classList.remove('with-custom-background')
        await transitionToPromise(this)
        this.removeZindexVariable()
      }
    }
  }

  showNotification = (text: string, variant: string, duration?: number) => {
    const notification: NotificationComponent = $el('#GlobalNotification')
    notification.show(text, variant, duration)
  }

  setScrollLock(): void {
    this.isFixed = true

    this.scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth

    if (this.scrollbarWidth && !isFirefox) {
      this.fixedTopPosition = window.scrollY || this.fixedTopPosition
      document.body.style.top = `-${this.fixedTopPosition}px`
      document.body.classList.add('scroll-y-off')
    } else {
      if (!this.isOpenedOnFirefox) {
        document.body.style.paddingRight = `${this.scrollbarWidth}px`
      }

      document.body.classList.add('scroll-y-off-firefox')
      this.isOpenedOnFirefox = true
    }
  }

  unsetScrollLock(): void {
    if (!this.isFixed) {
      return
    }

    this.isFixed = false

    if (this.scrollbarWidth && !isFirefox) {
      document.body.style.top = ''
      document.body.classList.remove('scroll-y-off')

      if (this.fixedTopPosition) {
        window.scrollTo(0, this.fixedTopPosition)
        this.fixedTopPosition = undefined
      }
    } else {
      document.body.style.paddingRight = ''
      document.body.classList.remove('scroll-y-off-firefox')
      this.isOpenedOnFirefox = false
    }
  }

  setOverlayStyles(styles = {}): void {
    Object.keys(styles).forEach(key => {
      this.style[key] = styles[key]
    })
  }

  setOverlayZindex(): void {
    this.setAttribute('style', `z-index: ${this.zIndex};`)
  }

  setZindexVariable = () => {
    setStyleVariable('body-overlay-zIndex', this.zIndex)
  }

  removeZindexVariable = () => {
    removeStyleVariable('body-overlay-zIndex')
  }

  setRegistrationMessage = (): void => {
    const isCaptchaPage = window.location.pathname.includes('challenge')
    const isAccountPage = window.location.pathname.includes('account')

    if (!isCaptchaPage && !isAccountPage) {
      if (localStorage.getItem('isRegistered')) {
        whenDefined('notification-component').then(() => {
          const message = window.auroraThemeLocales.registrationMessage
          const visibilityDuration = 10000
          const notification: NotificationComponent = $el('#GlobalNotification')

          notification.show(message, 'success', visibilityDuration)
          localStorage.removeItem('isRegistered')
        })
      }
    }
  }

  setGradientOverlay = () => {
    if (this.hasAttribute('data-gradient') && !this.withGradient) {
      this.parentElement.setAttribute(
        'style',
        `--gsc-gradient-overlay: ${gradientOpacity(
          this.dataset.gradient,
          this.dataset.opacity,
        )}`,
      )

      this.withGradient = true
    }
  }
}
